import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { toast } from 'sonner';
import { z } from 'zod';
import Honeybadger from '@honeybadger-io/js';
import { zodResolver } from '@hookform/resolvers/zod';

import { deserializer } from '../../../../../shared/utils/deserializer';
import { SUCCESS_MESSAGES } from '../constants';
import { fetchChannels as apiFetchChannels } from '../services/channelService';
import { createReviewRequest as apiCreateReviewRequest } from '../services/reviewRequestService';
import { fetchTasks as apiFetchTasks } from '../services/taskService';
import useReviewRequestDialog from './useReviewRequestDialog';
import useReviewsRequested from './useReviewsRequested';

const defaultValues = {
  channel: '',
  taskId: null,
};

const formSchema = z.object({
  channel: z.string().min(1, 'Channel is required.'),
  taskId: z.string().min(1, 'Task is required.'),
});

const useReviewRequestForm = () => {
  const [cases, setCases] = useState([]);
  const [channels, setChannels] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const { handleCloseDialog } = useReviewRequestDialog();
  const { fetchReviewsRequested } = useReviewsRequested();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    const fetchChannelsAndTasks = async () => {
      setIsLoading(true);

      try {
        const response = await Promise.all([apiFetchTasks(), apiFetchChannels()]);

        const cases = await deserializer.deserialize(response[0]);
        const { channels } = response[1];

        setCases(cases);
        setChannels(channels);
      } catch (error) {
        // Displaying the error may be confusing to the User. Instead, Honeybadger will
        // be notified so that the error can be investigated.
        Honeybadger.notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChannelsAndTasks();
  }, []);

  const handleSuccess = () => {
    fetchReviewsRequested();
    handleCloseDialog();
    toast.success(SUCCESS_MESSAGES.reviewRequest.create);
    form.reset();
  };

  const handleSubmit = async (formData) => {
    try {
      setErrorMessage('');
      setIsLoading(true);

      await apiCreateReviewRequest(formData);

      handleSuccess();
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      handleCloseDialog();
      setErrorMessage('');
      form.reset();
    }
  };

  return {
    cases,
    channels,
    errorMessage,
    form,
    handleOpenChange,
    handleSubmit,
    isLoading,
  };
};

export default useReviewRequestForm;
